import type { SharedModels } from '@boostbrothers/web-api';
import {
  ClientApiFactory,
  type LoginResModel,
} from '@boostbrothers/web-api/web-gateway-api';
import { Button, SimpleAlertModal } from '@boostbrothers/web-ui/web';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import ContentsEditorLogo from '~assets/graphics/graphic-logo.png';
import useLoading from '~hooks/useLoading';
import useModal from '~hooks/useModal';
import useToast from '~hooks/useToast';
import useViewTransition from '~hooks/useViewTransition';
import { getTemporaryContent } from '~utils/editor';
import { requestKakaoAccessToken, requestKakaoAuthCode } from '~utils/kakao';

const LoginPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateWithTransition } = useViewTransition();
  const { showLoading, hideLoading } = useLoading();
  const { addToast } = useToast();
  const { openModal, closeModal } = useModal();

  const { mutateAsync: loginWithKakao } = useMutation<
    LoginResModel,
    SharedModels.ErrorRes,
    string
  >({
    mutationFn: async (accessToken: string) => {
      const res = await ClientApiFactory('POST /v1/auth/cms')({
        payload: { access_token: accessToken },
      });

      return res;
    },
    onSuccess: ({ access_token }) => {
      localStorage.setItem('userToken', access_token);
      hideLoading();
      addToast({
        state: 'success',
        title: '로그인에 성공했습니다.',
        position: 'bottom-center',
      });

      const temporaryContent = getTemporaryContent();

      if (!temporaryContent) {
        navigateWithTransition('/');
        return;
      }

      navigateWithTransition(
        `/edit${temporaryContent.contentId ? `?contentId=${temporaryContent.contentId}` : ''}`
      );
    },
    onError: error => {
      let modalContent = '로그인을 실패했습니다. 잠시 후 다시 시도해주세요.';
      let modalSubText: string[] | undefined;

      if (error.error.status === 401) {
        modalContent = '권한 확인이 필요해요';
        modalSubText = ['권한을 부여받으시려면 똑닥 고객센터로 문의해 주세요.'];
      }
      if (error.error.status === 404) {
        modalContent = '똑닥 서비스에 가입된 계정만 접근 가능합니다.';
      }

      hideLoading();
      openModal({
        modalContent: (
          <SimpleAlertModal
            title={modalContent}
            description={modalSubText}
            confirmText="확인"
            confirmAction={closeModal}
          />
        ),
      });
    },
  });

  /**
   * @description 카카오 SDK 인가 코드를 사용하여, 똑닥 로그인 수행
   */
  useEffect(() => {
    const kakaoVerificationCode = searchParams.get('code');

    if (kakaoVerificationCode) {
      setSearchParams({}, { replace: true });
      showLoading();

      requestKakaoAccessToken(kakaoVerificationCode).then(res => {
        loginWithKakao(res.access_token);
      });
    }
  }, []);

  return (
    <main className="w-full h-screen flex items-center justify-center">
      <section className="w-[420px] h-[420px] bg-white rounded-xl p-6 flex flex-col items-center justify-between">
        <header className="flex flex-col items-center justify-between gap-y-4 mt-14">
          <img src={ContentsEditorLogo} alt="logo" width={223} height={76} />
          <p className="text-base text-CoolGray-900">똑닥 콘텐츠 에디터</p>
        </header>
        <Button
          label="카카오 로그인"
          colorType="primary"
          size="xl"
          className="w-full"
          onClick={requestKakaoAuthCode}
        />
      </section>
    </main>
  );
};

export default LoginPage;
